/**
 * 任务数据迁移工具
 * 支持从旧版本任务格式迁移到扩展任务格式
 */

import { Task } from '../types';
import { ExtendedTask, toExtendedTask } from '../types/extended-task';
import { logger } from '../utils/logger';
import fs from 'fs-extra';
import path from 'path';

export interface MigrationResult {
  success: boolean;
  totalTasks: number;
  migratedTasks: number;
  errors: string[];
  backupPath?: string;
}

export class TaskMigration {
  private tasksPath: string;
  private backupDir: string;

  constructor(workflowDir: string = process.cwd()) {
    this.tasksPath = path.join(workflowDir, '.workflow', 'tasks.json');
    this.backupDir = path.join(workflowDir, '.workflow', 'backups');
  }

  /**
   * 执行任务迁移
   */
  async migrate(): Promise<MigrationResult> {
    const result: MigrationResult = {
      success: false,
      totalTasks: 0,
      migratedTasks: 0,
      errors: []
    };

    try {
      // 1. 检查任务文件是否存在
      if (!await fs.pathExists(this.tasksPath)) {
        result.errors.push('任务文件不存在');
        return result;
      }

      // 2. 读取现有任务
      const tasksData = await fs.readJson(this.tasksPath);
      
      // 处理新的数据格式 { tasks: [...] }
      let tasks: Task[];
      if (Array.isArray(tasksData)) {
        tasks = tasksData;
      } else if (tasksData.tasks && Array.isArray(tasksData.tasks)) {
        tasks = tasksData.tasks;
      } else {
        tasks = [];
      }
      result.totalTasks = tasks.length;

      if (tasks.length === 0) {
        result.success = true;
        return result;
      }

      // 3. 创建备份
      const backupPath = await this.createBackup();
      result.backupPath = backupPath;
      logger.info(`Created backup at: ${backupPath}`);

      // 4. 迁移任务
      const migratedTasks: ExtendedTask[] = [];
      
      for (const task of tasks) {
        try {
          const migratedTask = this.migrateTask(task);
          migratedTasks.push(migratedTask);
          result.migratedTasks++;
        } catch (error: any) {
          result.errors.push(`Failed to migrate task ${task.id}: ${error.message}`);
          logger.error(`Migration error for task ${task.id}:`, error);
        }
      }

      // 5. 保存迁移后的任务（使用新格式）
      const newData = {
        tasks: migratedTasks,
        version: '2.0.0',
        migrated: true,
        migratedAt: new Date().toISOString()
      };
      await fs.writeJson(this.tasksPath, newData, { spaces: 2 });
      
      result.success = true;
      logger.info(`Successfully migrated ${result.migratedTasks} out of ${result.totalTasks} tasks`);

    } catch (error: any) {
      result.errors.push(`Migration failed: ${error.message}`);
      logger.error('Migration failed:', error);
      
      // 尝试恢复
      if (result.backupPath) {
        await this.restore(result.backupPath);
      }
    }

    return result;
  }

  /**
   * 迁移单个任务
   */
  private migrateTask(task: Task): ExtendedTask {
    // 基础转换
    const extendedTask = toExtendedTask(task);

    // 从 metadata 中提取可能已存在的扩展字段
    if (task.metadata) {
      // 检查是否已有规范关联
      if (task.metadata.specId) {
        extendedTask.specId = task.metadata.specId;
        delete task.metadata.specId;
      }
      
      if (task.metadata.designId) {
        extendedTask.designId = task.metadata.designId;
        delete task.metadata.designId;
      }
      
      if (task.metadata.workflowId) {
        extendedTask.workflowId = task.metadata.workflowId;
        delete task.metadata.workflowId;
      }
      
      if (task.metadata.prdId) {
        extendedTask.prdId = task.metadata.prdId;
        delete task.metadata.prdId;
      }

      // 检查 Agent 分配信息
      if (task.metadata.agentAssignments) {
        extendedTask.agentAssignments = task.metadata.agentAssignments;
        delete task.metadata.agentAssignments;
      }

      // 检查 Prompt 模板配置
      if (task.metadata.promptTemplates) {
        extendedTask.promptTemplates = task.metadata.promptTemplates;
        delete task.metadata.promptTemplates;
      }

      // 检查产出物
      if (task.metadata.artifacts) {
        extendedTask.artifacts = task.metadata.artifacts;
        delete task.metadata.artifacts;
      }

      // 智能推断一些字段
      this.inferExtendedFields(extendedTask, task);
    }

    return extendedTask;
  }

  /**
   * 智能推断扩展字段
   */
  private inferExtendedFields(extendedTask: ExtendedTask, originalTask: Task): void {
    // 根据任务内容推断可能的来源
    if (!extendedTask.metadata) {
      extendedTask.metadata = {};
    }

    // 推断任务来源
    if (!extendedTask.metadata.source) {
      if (originalTask.content.includes('PRD') || originalTask.content.includes('需求')) {
        extendedTask.metadata.source = 'prd';
      } else if (originalTask.content.includes('修复') || originalTask.content.includes('bug')) {
        extendedTask.metadata.source = 'manual';
        extendedTask.metadata.subtype = 'bugfix';
      } else {
        extendedTask.metadata.source = 'manual';
      }
    }

    // 推断技术栈
    if (!extendedTask.metadata.techStack) {
      const techKeywords = {
        'react': ['React', 'react', 'jsx', 'tsx'],
        'vue': ['Vue', 'vue'],
        'typescript': ['TypeScript', 'typescript', 'ts'],
        'nodejs': ['Node.js', 'node', 'express'],
        'python': ['Python', 'python', 'django', 'flask']
      };

      const techStack: string[] = [];
      for (const [tech, keywords] of Object.entries(techKeywords)) {
        if (keywords.some(keyword => originalTask.content.includes(keyword))) {
          techStack.push(tech);
        }
      }

      if (techStack.length > 0) {
        extendedTask.metadata.techStack = techStack;
      }
    }

    // 推断风险等级
    if (!extendedTask.metadata.riskLevel) {
      if (originalTask.priority === 'urgent' || originalTask.content.includes('紧急')) {
        extendedTask.metadata.riskLevel = 'high';
      } else if (originalTask.priority === 'high') {
        extendedTask.metadata.riskLevel = 'medium';
      } else {
        extendedTask.metadata.riskLevel = 'low';
      }
    }
  }

  /**
   * 创建备份
   */
  private async createBackup(): Promise<string> {
    await fs.ensureDir(this.backupDir);
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const backupName = `tasks-backup-${timestamp}.json`;
    const backupPath = path.join(this.backupDir, backupName);
    
    await fs.copy(this.tasksPath, backupPath);
    
    return backupPath;
  }

  /**
   * 从备份恢复
   */
  async restore(backupPath: string): Promise<void> {
    if (await fs.pathExists(backupPath)) {
      await fs.copy(backupPath, this.tasksPath, { overwrite: true });
      logger.info(`Restored from backup: ${backupPath}`);
    } else {
      logger.error(`Backup not found: ${backupPath}`);
    }
  }

  /**
   * 验证迁移结果
   */
  async validate(): Promise<{ valid: boolean; issues: string[] }> {
    const issues: string[] = [];
    
    try {
      const tasksData = await fs.readJson(this.tasksPath);
      
      // 处理新的数据格式 { tasks: [...] }
      let tasks: ExtendedTask[];
      if (Array.isArray(tasksData)) {
        tasks = tasksData;
      } else if (tasksData.tasks && Array.isArray(tasksData.tasks)) {
        tasks = tasksData.tasks;
      } else {
        tasks = [];
      }
      
      for (const task of tasks) {
        // 检查必需字段
        if (!task.id) issues.push(`Task missing ID`);
        if (!task.content) issues.push(`Task ${task.id} missing content`);
        if (!task.status) issues.push(`Task ${task.id} missing status`);
        if (!task.priority) issues.push(`Task ${task.id} missing priority`);
        
        // 检查日期格式
        if (task.createdAt && isNaN(Date.parse(task.createdAt as any))) {
          issues.push(`Task ${task.id} has invalid createdAt date`);
        }
        
        // 检查关联有效性
        if (task.dependsOn) {
          for (const depId of task.dependsOn) {
            if (!tasks.find(t => t.id === depId)) {
              issues.push(`Task ${task.id} depends on non-existent task ${depId}`);
            }
          }
        }
      }
      
    } catch (error: any) {
      issues.push(`Failed to validate: ${error.message}`);
    }
    
    return {
      valid: issues.length === 0,
      issues
    };
  }

  /**
   * 获取迁移状态
   */
  async getStatus(): Promise<{
    needsMigration: boolean;
    currentVersion: string;
    targetVersion: string;
    taskCount: number;
  }> {
    try {
      const tasksData = await fs.readJson(this.tasksPath);
      
      // 处理新的数据格式 { tasks: [...] }
      let tasks: any[];
      if (Array.isArray(tasksData)) {
        tasks = tasksData;
      } else if (tasksData.tasks && Array.isArray(tasksData.tasks)) {
        tasks = tasksData.tasks;
      } else {
        tasks = [];
      }
      
      // 检查是否需要迁移
      let needsMigration = false;
      let hasExtendedFields = false;
      
      for (const task of tasks) {
        // 检查是否有扩展字段
        if (task.specId || task.designId || task.workflowId || 
            task.agentAssignments || task.artifacts) {
          hasExtendedFields = true;
        }
        
        // 检查是否有旧格式的字段在 metadata 中
        if (task.metadata?.specId || task.metadata?.designId || 
            task.metadata?.workflowId || task.metadata?.agentAssignments) {
          needsMigration = true;
          break;
        }
      }
      
      // 检查是否已经是 2.0 格式
      const isV2Format = 'version' in tasksData && tasksData.version === '2.0.0';
      
      return {
        needsMigration: needsMigration || !isV2Format,
        currentVersion: isV2Format ? '2.0.0' : (hasExtendedFields ? '2.0' : '1.0'),
        targetVersion: '2.0.0',
        taskCount: tasks.length
      };
      
    } catch (error) {
      return {
        needsMigration: false,
        currentVersion: 'unknown',
        targetVersion: '2.0',
        taskCount: 0
      };
    }
  }
}